import { HttpErrorResponse } from '@angular/common/http';
import { createReducer, on } from '@ngrx/store';
import { CustomFieldAppliesTo, CustomFieldDefinition } from 'brain-data/model/custom-field.model';
import {
  createCustomFieldDefinitionSuccess,
  deleteCustomFieldDefinitionSuccess,
  loadCustomFieldDefinitions,
  loadCustomFieldDefinitionsError,
  loadCustomFieldDefinitionsSuccess,
  updateCustomFieldDefinitionSuccess,
} from '../actions/custom-field-definition.action';

export default interface CustomFieldDefinitionState {
  /** The subject the list currently shows; the API is `appliesTo`-scoped and so is this state. */
  appliesTo: CustomFieldAppliesTo;
  definitions: CustomFieldDefinition[];
  loading: boolean;
  loaded: boolean;
  error?: HttpErrorResponse;
}

export const initialState: CustomFieldDefinitionState = {
  appliesTo: 'FACILITY',
  definitions: [],
  loading: false,
  loaded: false,
  error: undefined,
};

/** The server orders by label (`findByAppliesToOrderByLabelAsc`); the client keeps that order. */
const byLabel = (a: CustomFieldDefinition, b: CustomFieldDefinition) => a.label.localeCompare(b.label);

/**
 * A row is only ever admitted when it governs the subject on screen. A create or an update always
 * targets the visible subject, but the answer arrives asynchronously — the administrator may have
 * switched by then, and showing a BUILDING field in a FACILITY list would claim a scope the list
 * does not have.
 */
const admit = (
  state: CustomFieldDefinitionState,
  definition: CustomFieldDefinition,
): CustomFieldDefinition[] =>
  definition.appliesTo === state.appliesTo
    ? [...state.definitions.filter(d => d.id !== definition.id), definition].sort(byLabel)
    : state.definitions;

export const reducer = createReducer(
  initialState,

  on(loadCustomFieldDefinitions, (state, { appliesTo }): CustomFieldDefinitionState => ({
    ...state,
    appliesTo,
    definitions: appliesTo === state.appliesTo ? state.definitions : [],
    loading: true,
    error: undefined,
  })),

  // A load answer for a subject that is no longer selected is DROPPED rather than painted over the
  // list on screen: `switchMap` cancels the in-flight request, but an answer already in flight when
  // the switch happened would otherwise land on the wrong subject.
  on(loadCustomFieldDefinitionsSuccess, (state, { appliesTo, definitions }): CustomFieldDefinitionState =>
    appliesTo === state.appliesTo
      ? { ...state, definitions: [...definitions].sort(byLabel), loading: false, loaded: true }
      : state,
  ),

  on(loadCustomFieldDefinitionsError, (state, { error }): CustomFieldDefinitionState => ({
    ...state,
    loading: false,
    error,
  })),

  on(createCustomFieldDefinitionSuccess, (state, { definition }): CustomFieldDefinitionState => ({
    ...state,
    definitions: admit(state, definition),
  })),

  on(updateCustomFieldDefinitionSuccess, (state, { definition }): CustomFieldDefinitionState => ({
    ...state,
    definitions: admit(state, definition),
  })),

  on(deleteCustomFieldDefinitionSuccess, (state, { id }): CustomFieldDefinitionState => ({
    ...state,
    definitions: state.definitions.filter(d => d.id !== id),
  })),
);
